// Loading, empty and error states. An API failure is worded by what staff can do about it.
// Empty and ErrorState are Console D's EmptyNote and ErrorState (@apsio/ui, APS-412): one calm
// note or one quiet line in the section, never a box.
import { EmptyNote, ErrorState as ErrorLine } from '@apsio/ui'
import type { ReactNode } from 'react'
import { API_BASE, isApiError } from '../api/client'
import { type MessageId, useI18n } from '../i18n'

export function Loading() {
  const { t } = useI18n()
  return (
    <p role="status" className="mono dim" style={{ fontSize: 13, padding: '14px 18px' }}>
      {t('state.loading')}
    </p>
  )
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return <EmptyNote title={title}>{children}</EmptyNote>
}

const BY_CODE: Record<string, MessageId> = {
  mfa_required: 'state.mfaRequired',
  session_expired: 'state.sessionExpired',
  reauth_required: 'state.reauth',
  forbidden: 'state.forbidden',
  not_found: 'state.notFound',
  conflict: 'state.conflict',
  invalid_request: 'state.invalid',
  invalid: 'state.invalid',
  unauthorized: 'state.signedOut',
}

/** The message for an API error; the server's own text follows for conflicts and refusals. */
export function errorMessage(t: ReturnType<typeof useI18n>['t'], error: unknown): string {
  if (isApiError(error)) return t(BY_CODE[error.code] ?? 'state.failed')
  return t('state.failed')
}

/**
 * The server's own words for a refusal (409), and the way back for a step-up: the hosted page
 * asks for the credentials again (max_age=0), so the new sign-in counts as recent.
 */
export function ErrorDetail({ error }: { error: unknown }) {
  const { t } = useI18n()
  if (isApiError(error) && error.code === 'reauth_required')
    return (
      <a className="btn sm" href={`${API_BASE}/auth/sign-in?reauth=1`}>
        {t('state.signInAgain')}
      </a>
    )
  if (isApiError(error) && error.status === 409)
    return (
      <p className="mono" style={{ fontSize: 12.5 }} translate="no">
        {error.message}
      </p>
    )
  return null
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const { t } = useI18n()
  return (
    <ErrorLine
      message={errorMessage(t, error)}
      detail={<ErrorDetail error={error} />}
      retry={retry && !isApiError(error, 403) && !isApiError(error, 404) ? retry : undefined}
      retryLabel={t('state.retry')}
    />
  )
}
